import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchShippingQuote } from '../../../actions'
import {
  fetchShippingRates,
  setSelectedRateForGroupAndRecalculateTax
} from '../../../redux/storeConfig/slices/slice.checkoutRates'
import { shouldCalculateShipping, createShippingParams } from '../services/shippingCalculationService'
import { buildShippingParams } from '../services/shippingParamsBuilder'
import { createDigitalDeliveryRate, DIGITAL_DELIVERY_GROUP_ID } from '../ShippingMethodSelector/shippingGroupsHelpers'
import { useShipperHQCheck } from './useShipperHQCheck'

/**
 * Custom hook to handle shipping calculation
 * @param {object} params - Shipping calculation parameters
 * @param {boolean} params.isInitializing - Whether checkout is initializing
 * @param {boolean} params.isCartLoaded - Whether cart is loaded
 * @param {Array} params.cartItems - Array of cart items
 * @param {object} params.checkout - Checkout state object
 * @param {object} params.portal - Portal configuration
 * @param {object} params.integrations - Integrations configuration
 * @param {object} params.checkoutRates - Checkout rates state
 * @returns {object} - { calculateShipping: function }
 */
export const useShippingCalculation = ({
  isInitializing,
  isCartLoaded,
  cartItems,
  checkout,
  portal,
  integrations,
  checkoutRates
}) => {
  const currentUser = useSelector(state => state.currentUser)
  const dispatch = useDispatch()
  const { selectedCustomerAddress, multiAddressSelection } = checkout || {}
  const { isShipperHQ, isShipEngine, isFulfillmentIntegrationUnknown } = useShipperHQCheck(integrations)

  // Create stable serialized keys for complex objects/arrays
  const selectedAddressKey = useMemo(() => JSON.stringify(selectedCustomerAddress), [selectedCustomerAddress])
  const multiAddressSelectionKey = useMemo(() => JSON.stringify(multiAddressSelection), [multiAddressSelection])
  const cartItemsKey = useMemo(() => JSON.stringify(cartItems), [cartItems])
  const integrationsDataKey = useMemo(() => JSON.stringify(integrations?.data), [integrations?.data])

  const calculateShipping = useCallback(() => {
    // Wait for integrations to load before making shipping flow decisions
    if (integrations?.status !== 'succeeded') return
    if (isFulfillmentIntegrationUnknown) return

    // Skip fetching rates if third party billing is selected
    const shippingPaymentMethod = checkoutRates?.shippingPaymentMethod || 'standard'
    if (shippingPaymentMethod === 'third_party_billing') return

    if (isShipperHQ) {
      const shouldCalculate = shouldCalculateShipping({
        isInitializing,
        isCartLoaded,
        checkout,
        portal,
        isShipperHQ
      })

      if (!shouldCalculate) return

      const {
        selectedCustomerAddress,
        cartItems: items,
        multiAddressSelection
      } = createShippingParams({
        checkout,
        cartItems
      })

      dispatch(fetchShippingQuote(selectedCustomerAddress, items, multiAddressSelection))
      return
    }

    if (!isShipEngine || isInitializing || !isCartLoaded || !cartItems?.length) return

    const shippableItems = cartItems.filter(item => item.shippable_type !== 'Digital Delivery')
    const digitalItems = cartItems.filter(item => item.shippable_type === 'Digital Delivery')

    // Handle digital delivery items - create shipping data so server can process them
    if (digitalItems.length > 0) {
      const digitalRate = createDigitalDeliveryRate(digitalItems)
      dispatch(setSelectedRateForGroupAndRecalculateTax(DIGITAL_DELIVERY_GROUP_ID, digitalRate))
    }

    if (shippableItems.length === 0) return
    if (checkoutRates?.isCalculating) return

    // Guest users always use single-address shipping, even on multi-address portals
    const isMultiAddressEnabled = portal?.multiple_address_shipping && !!currentUser?.id

    const params = buildShippingParams({
      selectedAddress: selectedCustomerAddress,
      cartItems: shippableItems,
      multiAddressSelection,
      isMultiAddressEnabled
    })

    if (params) {
      dispatch(fetchShippingRates(params))
    }
  }, [
    isInitializing,
    isCartLoaded,
    selectedAddressKey,
    multiAddressSelectionKey,
    cartItemsKey,
    integrations?.status,
    integrationsDataKey,
    isFulfillmentIntegrationUnknown,
    isShipperHQ,
    isShipEngine,
    portal?.multiple_address_shipping,
    currentUser?.id,
    checkoutRates?.isCalculating,
    checkoutRates?.shippingPaymentMethod,
    dispatch
  ])

  return { calculateShipping }
}
